import { AccessRole, Permission } from '../@types/shared.types';
import {
    AppointmentPermission,
    AppointmentStatus,
    ClientPermission,
    ClinicalTechnique,
    GeneralPermission,
    GroupEventStatus,
    GroupSessionStatus,
    MessageStatus,
    ProviderPermission,
    UserRole,
} from '../enums/shared.enums';
import { t as translate } from 'i18next';

/**
 * Shared constants
 */

export const CONVERSATION_PAGINATION_LIMIT = 10;
export const MIN_USER_AGE = 13;
export const MINOR_USER_AGE = 18;

export const MARKETING_URL = 'reachlink.com';
export const JOIN_SESSION_URL = 'https://app1.reachlink.com/ml/join-session/';
export const APP_DOWNLOAD_URL = 'https://app1.reachlink.com/ml/app-download/';

export const REACHLINK_EMAIL = 'hello@reachlink.com';
export const REACHLINK_CARE_EMAIL = 'care@reachlink.com';
export const REACHLINK_CS_PHONE = '+18333220977';
export const REACHLINK_CARE_PHONE = '833-588-7992';
export const REACHLINK_FAX_NUMBER = '+18334910468';
export const REACHLINK_ADMIN_PHONE = '+18337322489';

export const APPOINTMENT_JOIN_BUFFERS = {
    start: 15,
    end: 0,
};

export const MAX_CUSTOM_CHARGE_AMOUNT = 30000;

export const APPOINTMENT_CHARGE_LEGACY_COMP_PREFIX = 'ReachLink Session';
export const APPOINTMENT_CHARGE_DESCRIPTION_PREFIX = 'ReachLink session scheduled for';
export const APPOINTMENT_LATE_FEE_DESCRIPTION_PREFIX =
    'ReachLink same-day cancellation fee for session on';
export const APPOINTMENT_CHARGE_CUSTOM_PAYMENT_PREFIX =
    'Reachlink additional charge for session on';

export const PROVIDER_AV_WINDOW_DAYS = 60;

export const CLIENT_SUSPENDED_STATUS = 'Suspended';

export const PROVIDER_CASE_LOAD_MONTH_WINDOW = 2;

export const LOW_PAYING_CLAIM_CUTOFF = 5900;

export const HIGH_PAYING_CLAIM_CUTOFF = 30000;

export const FAILED_MESSAGE_STATUSES = [
    MessageStatus.UNDELIVERED,
    MessageStatus.FAILED,
    MessageStatus.BOUNCED,
    MessageStatus.DROPPED,
];

export const AMBIGUOUS_MESSAGE_STATUSES = [MessageStatus.DELIVERY_UNKNOWN];

/**@deprecated - please fetch the sessionName or displayName from the backend instead of using this for participant's sessionName or displayName*/
export const SESSION_USER_NAME_FORMAT = (
    user: { firstName?: string; nickName?: string } | null,
    roleName?: UserRole
) => (user?.nickName || user?.firstName || roleName)?.trim();

interface CurrencySymbol {
    [key: string]: string;
    usd: string;
    cad: string;
    eur: string;
    gbp: string;
}

export const CURRENCY_SYMBOLS: CurrencySymbol = {
    usd: '$',
    cad: '$',
    eur: '€',
    gbp: '£',
    zar: 'R',
    aud: '$',
    php: '₱',
};

export const ClinicalTechniqueDescriptions = {
    [ClinicalTechnique.MOOD_CHECK]: {
        short: translate('constants.shared.clinicalTechnique.moodCheck.short'),
        medium: translate('constants.shared.clinicalTechnique.moodCheck.medium'),
        long: translate('constants.shared.clinicalTechnique.moodCheck.long'),
        long2: translate('constants.shared.clinicalTechnique.moodCheck.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.moodCheck.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.moodCheck.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.moodCheck.shortFlex'),
    },
    [ClinicalTechnique.BRIDGE]: {
        short: translate('constants.shared.clinicalTechnique.bridge.short'),
        medium: translate('constants.shared.clinicalTechnique.bridge.medium'),
        long: translate('constants.shared.clinicalTechnique.bridge.long'),
        long2: translate('constants.shared.clinicalTechnique.bridge.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.bridge.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.bridge.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.bridge.shortFlex'),
    },
    [ClinicalTechnique.AGENDA_SETTING]: {
        short: translate('constants.shared.clinicalTechnique.agendaSetting.short'),
        medium: translate('constants.shared.clinicalTechnique.agendaSetting.medium'),
        long: translate('constants.shared.clinicalTechnique.agendaSetting.long'),
        long2: translate('constants.shared.clinicalTechnique.agendaSetting.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.agendaSetting.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.agendaSetting.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.agendaSetting.shortFlex'),
    },
    [ClinicalTechnique.CBT_SKILLS]: {
        short: translate('constants.shared.clinicalTechnique.cbtSkills.short'),
        medium: translate('constants.shared.clinicalTechnique.cbtSkills.medium'),
        long: translate('constants.shared.clinicalTechnique.cbtSkills.long'),
        long2: translate('constants.shared.clinicalTechnique.cbtSkills.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.cbtSkills.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.cbtSkills.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.cbtSkills.shortFlex'),
    },
    [ClinicalTechnique.ACTION_PLAN]: {
        short: translate('constants.shared.clinicalTechnique.actionPlan.short'),
        medium: translate('constants.shared.clinicalTechnique.actionPlan.medium'),
        long: translate('constants.shared.clinicalTechnique.actionPlan.long'),
        long2: translate('constants.shared.clinicalTechnique.actionPlan.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.actionPlan.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.actionPlan.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.actionPlan.shortFlex'),
    },
    [ClinicalTechnique.SESSION_SUMMARY]: {
        short: translate('constants.shared.clinicalTechnique.sessionSummary.short'),
        medium: translate('constants.shared.clinicalTechnique.sessionSummary.medium'),
        long: translate('constants.shared.clinicalTechnique.sessionSummary.long'),
        long2: translate('constants.shared.clinicalTechnique.sessionSummary.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.sessionSummary.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.sessionSummary.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.sessionSummary.shortFlex'),
    },
    [ClinicalTechnique.SOCRATIC_QUESTIONING]: {
        short: translate('constants.shared.clinicalTechnique.socraticQuestioning.short'),
        medium: translate('constants.shared.clinicalTechnique.socraticQuestioning.medium'),
        long: translate('constants.shared.clinicalTechnique.socraticQuestioning.long'),
        long2: translate('constants.shared.clinicalTechnique.socraticQuestioning.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.socraticQuestioning.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.socraticQuestioning.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.socraticQuestioning.shortFlex'),
    },
    [ClinicalTechnique.REFLECTIVE_LISTENING]: {
        short: translate('constants.shared.clinicalTechnique.reflectiveListening.short'),
        medium: translate('constants.shared.clinicalTechnique.reflectiveListening.medium'),
        long: translate('constants.shared.clinicalTechnique.reflectiveListening.long'),
        long2: translate('constants.shared.clinicalTechnique.reflectiveListening.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.reflectiveListening.longFlex'),
        mediumFlex: translate('constants.shared.clinicalTechnique.reflectiveListening.mediumFlex'),
        shortFlex: translate('constants.shared.clinicalTechnique.reflectiveListening.shortFlex'),
    },
    [ClinicalTechnique.EMPATHIC_COMMUNICATION]: {
        short: translate('constants.shared.clinicalTechnique.empathicCommunication.short'),
        medium: translate('constants.shared.clinicalTechnique.empathicCommunication.medium'),
        long: translate('constants.shared.clinicalTechnique.empathicCommunication.long'),
        long2: translate('constants.shared.clinicalTechnique.empathicCommunication.long2'),
        longFlex: translate('constants.shared.clinicalTechnique.empathicCommunication.longFlex'),
        mediumFlex: translate(
            'constants.shared.clinicalTechnique.empathicCommunication.mediumFlex'
        ),
        shortFlex: translate('constants.shared.clinicalTechnique.empathicCommunication.shortFlex'),
    },
};

/*
 * Permissions
 */

export const ROLE_PERMISSION_MAP: {
    [key in Permission]: AccessRole[];
} = {
    /*
     * Read
     */

    // Appointments
    [AppointmentPermission.READ_GENERAL]: ['any'],
    [AppointmentPermission.READ_ZOOM]: ['any'],
    [AppointmentPermission.READ_PAYMENTS]: ['any'],
    [AppointmentPermission.READ_SESSION_DATA]: [
        UserRole.CC_MANAGER,
        UserRole.CARE_COORDINATOR,
        UserRole.SUPPORT_SPECIALIST,
    ],
    [AppointmentPermission.READ_TRANSCRIPTIONS]: [],
    [AppointmentPermission.READ_DIAGNOSES]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [AppointmentPermission.READ_NOTES]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    // For appointment action permissions, see src/constants/common.constants.ts/APPOINTMENT_ACTION_PERMISSION_MAP

    // Clients
    [ClientPermission.READ_GENERAL]: ['any'],
    [ClientPermission.READ_INTAKE]: [
        UserRole.RECRUITER,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [ClientPermission.READ_ASSESSMENTS]: ['any'],
    [ClientPermission.READ_JOURNALS]: [],
    [ClientPermission.READ_PLANS]: [],
    [ClientPermission.READ_PAYMENT_METHODS]: ['any'],
    [ClientPermission.READ_PAYMENTS]: ['any'],
    [ClientPermission.READ_POLICIES]: ['any'],
    [ClientPermission.READ_CLAIMS]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [ClientPermission.READ_UPLOADS]: ['any'],
    [ClientPermission.READ_CONVERSATIONS]: ['any'],
    [ClientPermission.READ_CAREBOT_CONVERSATIONS]: [],
    [ClientPermission.READ_ASSIGNMENT_SUBMISSIONS]: ['any'],
    [ClientPermission.READ_TRUSTED_CONTACTS]: ['any'],

    // Providers
    [ProviderPermission.READ_GENERAL]: ['any'],
    [ProviderPermission.READ_AVAILABILITY]: ['any'],
    [ProviderPermission.READ_CONVERSATIONS]: ['any'],
    [ProviderPermission.READ_PAYOUTS]: ['any'],
    [ProviderPermission.READ_ANALYTICS]: ['any'],
    [ProviderPermission.READ_CREDENTIALING_DATA]: [
        UserRole.RECRUITER,
        UserRole.CREDENTIALER,
        UserRole.BILLING_ADMIN,
    ],
    [ProviderPermission.READ_UPLOADS]: ['any'],

    /*
     * Write
     */

    // General
    [GeneralPermission.WRITE_CARRIERS]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    // Only super admins (who bypass the permission map) may manage other admins.
    [GeneralPermission.WRITE_ADMINS]: [],
    [GeneralPermission.DELETE_USERS]: [],

    // Appointments
    [AppointmentPermission.WRITE_DIAGNOSES]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [AppointmentPermission.WRITE_FIRST_TIME_DIAGNOSES]: [],
    [AppointmentPermission.WRITE_NOTES]: [],

    // Clients
    [ClientPermission.WRITE_GENERAL]: ['any'],
    [ClientPermission.WRITE_INTAKE]: [UserRole.NETWORK_PARTNER_ADMIN],
    [ClientPermission.WRITE_CREATION]: [
        UserRole.CC_MANAGER,
        UserRole.CARE_COORDINATOR,
        UserRole.SUPPORT_SPECIALIST,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [ClientPermission.WRITE_REFUNDS]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [ClientPermission.WRITE_CLAIMS]: [UserRole.BILLING_ADMIN, UserRole.NETWORK_PARTNER_ADMIN],
    [ClientPermission.WRITE_UPLOADS]: ['any'],
    [ClientPermission.WRITE_PAYMENT_METHODS]: [
        UserRole.CC_MANAGER,
        UserRole.BILLING_ADMIN,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [ClientPermission.WRITE_POLICIES]: [
        UserRole.BILLING_ADMIN,
        UserRole.CC_MANAGER,
        UserRole.CARE_COORDINATOR,
        UserRole.SUPPORT_SPECIALIST,
        UserRole.NETWORK_PARTNER_ADMIN,
    ],
    [ClientPermission.WRITE_CONVERSATIONS]: [],
    [ClientPermission.WRITE_BETA_ACCESS]: [
        UserRole.CC_MANAGER,
        UserRole.CARE_COORDINATOR,
        UserRole.SUPPORT_SPECIALIST,
        UserRole.RECRUITER,
    ],
    [ClientPermission.WRITE_ASSIGNMENT_SUBMISSIONS]: ['any'],
    [ClientPermission.WRITE_TRUSTED_CONTACTS]: [
        UserRole.CARE_COORDINATOR,
        UserRole.SUPPORT_SPECIALIST,
        UserRole.CC_MANAGER,
    ],

    // Providers
    [ProviderPermission.WRITE_GENERAL]: ['any'],
    [ProviderPermission.WRITE_CREATION]: [UserRole.RECRUITER],
    [ProviderPermission.WRITE_CREDENTIALING_DATA]: [UserRole.RECRUITER, UserRole.CREDENTIALER],
    [ProviderPermission.WRITE_AVAILABILITY]: ['any'],
    [ProviderPermission.WRITE_UPLOADS]: ['any'],
    [ProviderPermission.WRITE_RECRUITING]: [UserRole.RECRUITER],

    /*
     * Delete
     */

    // Clients
    [ClientPermission.DELETE_POLICIES]: [UserRole.CC_MANAGER],

    /*
     * Special
     */
    [AppointmentPermission.UNLOCK_DIAGNOSES]: [UserRole.CC_MANAGER],
    [ProviderPermission.FORCE_AVAILABILITY]: [UserRole.CC_MANAGER],
};

/*
 * Groups
 */

export const GROUPS_PROFITABILITY_THRESHOLD = 100;

export const APPOINTMENT_TO_GROUP_STATUS_MAP: Record<
    AppointmentStatus,
    GroupSessionStatus | undefined
> = {
    [AppointmentStatus.CONFIRMED]: GroupSessionStatus.CONFIRMED,
    [AppointmentStatus.OCCURRED]: GroupSessionStatus.COMPLETED,
    [AppointmentStatus.CANCELED]: GroupSessionStatus.CANCELED,
    [AppointmentStatus.MISSED_PROVIDER]: GroupSessionStatus.MISSED_BY_PROVIDER,
    [AppointmentStatus.MISSED_CLIENT]: undefined,
    [AppointmentStatus.PENDING]: undefined,
};

export const ACTIVE_GROUP_STATUSES: GroupEventStatus[] = [
    GroupEventStatus.APPROVED,
    GroupEventStatus.ACTIVE,
    GroupEventStatus.REACTIVATED,
];
